import type { HelpCenterHomeSection } from "@productlane/lib/src/help-center-home-sections"
import type { HelpCenterCustomHomeSection } from "@productlane/zero"
import {
	dedupeCustomHomeSections,
	homeSectionLimit,
} from "@productlane/lib/src/help-center-home-sections"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"

import type { ArticleWithSubpages, GroupWithArticles } from "./types"
import { isPublicTreeRow } from "./article-kind"

export const HOME_SECTION_LABEL: Record<HelpCenterHomeSection, string> = {
	GET_STARTED: "Get started",
	POPULAR: "Popular",
}

/** The docs home reads the same order, so the dashboard has to sort the same way. */
export function articlesInHomeSection<
	T extends {
		homeSection?: string | null
		homeOrder?: number | null
		order: number
	},
>(articles: readonly T[], section: HelpCenterHomeSection): T[] {
	return articles
		.filter((article) => article.homeSection === section)
		.sort(
			(a, b) =>
				(a.homeOrder ?? Number.MAX_SAFE_INTEGER) -
					(b.homeOrder ?? Number.MAX_SAFE_INTEGER) || a.order - b.order,
		)
		.slice(0, homeSectionLimit(section))
}

/** Everything a team can still put on the home: public pages, no subpages. */
export function homeSectionCandidates(
	articles: readonly ArticleWithSubpages[],
): ArticleWithSubpages[] {
	return articles.filter(
		(article) =>
			isPublicTreeRow(article) &&
			!article.parentArticleId &&
			!article.isDeleted &&
			!article.homeSection &&
			!article.customHomeSectionId,
	)
}

export interface CustomHomeSectionView {
	section: HelpCenterCustomHomeSection
	/** Set for a folder section. */
	group: GroupWithArticles | null
	title: string
	/** What readers see, minus anything a section above already shows. */
	articles: ArticleWithSubpages[]
}

const byHomeOrder = (a: ArticleWithSubpages, b: ArticleWithSubpages) =>
	(a.homeOrder ?? Number.MAX_SAFE_INTEGER) -
		(b.homeOrder ?? Number.MAX_SAFE_INTEGER) || a.order - b.order

const onHome = (article: ArticleWithSubpages) =>
	isPublicTreeRow(article) && !article.isDeleted && !article.parentArticleId

/** Mirrors the docs home: same order, same folders, same dedupe as the portal read. */
export function customHomeSectionViews({
	sections,
	articleGroups,
	portalInstanceId,
	shownAbove,
}: {
	sections: readonly HelpCenterCustomHomeSection[]
	articleGroups: readonly GroupWithArticles[]
	portalInstanceId: string | null
	shownAbove: readonly ArticleWithSubpages[]
}): CustomHomeSectionView[] {
	const all = articleGroups.flatMap((group) => group.helpCenterArticles)

	const views = [...sections]
		.filter(
			(section) => (section.portalInstanceId ?? null) === portalInstanceId,
		)
		.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt)
		.flatMap((section): CustomHomeSectionView[] => {
			if (!section.groupId) {
				return [
					{
						section,
						group: null,
						title: section.name ?? "",
						articles: all
							.filter(
								(article) =>
									article.customHomeSectionId === section.id && onHome(article),
							)
							.sort(byHomeOrder),
					},
				]
			}
			// A folder that left this portal, or was deleted, leaves nothing to show.
			const group = articleGroups.find((g) => g.id === section.groupId)
			if (!group) return []
			return [
				{
					section,
					group,
					title: section.name || group.name,
					articles: group.helpCenterArticles
						.filter(onHome)
						.sort((a, b) => a.order - b.order),
				},
			]
		})

	const kept = new Map(
		dedupeCustomHomeSections(
			views.map((view) => ({
				id: view.section.id,
				title: view.title,
				articles: view.articles,
			})),
			(article: ArticleWithSubpages) => article.id,
			shownAbove.map((article) => article.id),
		).map((row) => [row.id, row.articles]),
	)
	return views.map((view) => ({
		...view,
		articles: [...(kept.get(view.section.id) ?? [])],
	}))
}

/** Folders a new or switched folder section can use: named, unused, and with something readers see. */
export function pickableHomeFolders(
	groups: readonly GroupWithArticles[],
	views: readonly CustomHomeSectionView[],
): GroupWithArticles[] {
	const used = new Set(views.map((view) => view.section.groupId))
	return groups.filter(
		(group) =>
			!used.has(group.id) &&
			!isLooseGroup(group) &&
			group.helpCenterArticles.some(onHome),
	)
}

const CURATED_HOME_SECTIONS: readonly HelpCenterHomeSection[] = [
	"GET_STARTED",
	"POPULAR",
]

/** Get started and Popular leave the home when empty, like on the portal, until someone adds them back. */
export function hiddenCuratedSections(
	articles: Record<HelpCenterHomeSection, readonly unknown[]>,
	added: ReadonlySet<HelpCenterHomeSection>,
): HelpCenterHomeSection[] {
	return CURATED_HOME_SECTIONS.filter(
		(section) => articles[section].length === 0 && !added.has(section),
	)
}
